import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { Supplier } from 'api/supplier/suppliers.types'
import { supplierGroupsAPI } from 'api/supplierGroup/supplierGroupAPI'
import { SupplierVehicle } from 'api/supplierGroup/suppliergroup.types'
import { taxisAPI } from 'api/vehicle/taxisAPI'
import { Taxi } from 'api/vehicle/taxisAPI.types'
import { FC, useEffect, useState } from 'react'

interface VehicleMappingProps {
  supplier: Supplier
  onBack?: () => void
  onComplete?: () => void
  onCredentialError?: () => void
}

const VehicleMapping: FC<VehicleMappingProps> = ({
  supplier,
  onBack,
  onComplete,
  onCredentialError,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [vehicles, setVehicles] = useState<Taxi[]>([])
  const [platformVehicles, setPlatformVehicles] = useState<SupplierVehicle[]>(
    [],
  )
  const [mappings, setMappings] = useState<Record<number, string>>({})
  const [fetchError, setFetchError] = useState<string>()
  const [saveError, setSaveError] = useState<string>()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [taxisResponse, optionsResponse, supplierGroupResponse] =
          await Promise.all([
            taxisAPI.getTaxisBySupplierId(supplier.id),
            supplierGroupsAPI.retrieveSupplierVehicleOptions(
              supplier.supplierGroupId!,
              supplier.id,
            ),
            supplierGroupsAPI.getSupplierGroupById(supplier.supplierGroupId!),
          ])

        const taxis = taxisResponse.data.value
        setVehicles(taxis)
        setPlatformVehicles(optionsResponse.data.value)

        const existing: Record<number, string> = {}

        taxis.forEach((t) => {
          if (
            t.id &&
            t.supplierReference !== undefined &&
            t.supplierReference !== null
          ) {
            existing[t.id] = String(t.supplierReference)
          }
        })

        const defaults = supplierGroupResponse.data.defaultVehicleMappings ?? []
        taxis.forEach((t) => {
          if (t.id && !existing[t.id]) {
            const match = defaults.find(
              (d) => d.vehicleTypeName === t.vehicleDetails?.vehicleTypeName,
            )
            if (match) {
              existing[t.id] = String(match.supplierReference)
            }
          }
        })

        setMappings(existing)
      } catch {
        setFetchError('Failed to load vehicle data.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [supplier.id, supplier.supplierGroupId])

  const allMapped =
    vehicles.length > 0 && vehicles.every((v) => v.id && mappings[v.id])

  const handleMappingChange = (taxiId: number, supplierReference: string) => {
    setMappings((prev) => ({ ...prev, [taxiId]: supplierReference }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(undefined)
    try {
      await Promise.all(
        vehicles
          .filter((v) => v.id && mappings[v.id!])
          .map((v) =>
            taxisAPI.updateTaxi(v.id!, {
              supplierReference: mappings[v.id!],
            } as any),
          ),
      )
      onComplete?.()
    } catch {
      setSaveError('Failed to save vehicle mappings. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 2 }}>
        <CircularProgress size={24} />
        <Typography>Loading vehicle mapping data...</Typography>
      </Box>
    )
  }

  if (fetchError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Alert severity="warning">
          Failed to load vehicle data. This usually means the integration
          credentials are no longer valid. Please check and re-enter your
          credentials.
        </Alert>
        {onCredentialError && (
          <Box>
            <Button variant="contained" onClick={onCredentialError}>
              Re-enter Credentials
            </Button>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormLabel>Vehicle Mappings</FormLabel>
      <Typography variant="body2" color="text.secondary">
        Map each of your vehicles to the corresponding dispatch platform vehicle
        type.
      </Typography>

      {platformVehicles.length === 0 && Object.keys(mappings).length === 0 && (
        <Alert severity="warning">
          No vehicle options were returned from the dispatch platform. Please
          check your integration credentials.
        </Alert>
      )}

      {(platformVehicles.length > 0 || Object.keys(mappings).length > 0) && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Your Vehicle</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Luggage</TableCell>
                <TableCell>Integration Vehicle</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles.map((v) => (
                <TableRow key={v.id}>
                  <TableCell>{v.vehicleDetails.vehicleTypeName}</TableCell>
                  <TableCell>{v.vehicleDetails.seats}</TableCell>
                  <TableCell>{v.vehicleDetails.luggage}</TableCell>
                  <TableCell sx={{ minWidth: 250 }}>
                    <Select
                      size="small"
                      fullWidth
                      value={mappings[v.id!] ?? ''}
                      onChange={(e) =>
                        handleMappingChange(v.id!, e.target.value as string)
                      }
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select...</em>
                      </MenuItem>
                      {platformVehicles.map((pv) => (
                        <MenuItem
                          key={pv.supplierReference}
                          value={pv.supplierReference}
                        >
                          {`${pv.supplierReference} ${
                            pv.description ? ` ${pv.description}` : ''
                          }${
                            pv.numberOfSeats
                              ? ` - Seats: ${pv.numberOfSeats}`
                              : ''
                          }${
                            pv.numberOfCases
                              ? ` - Bags: ${pv.numberOfCases}`
                              : ''
                          }`}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    {mappings[v.id!] && (
                      <CheckCircleOutlineIcon
                        color="success"
                        fontSize="small"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {saveError && <Typography color="error">{saveError}</Typography>}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {onBack && (
          <Button onClick={onBack} disabled={isSaving}>
            Back
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!allMapped || isSaving}
        >
          {isSaving ? <CircularProgress size={20} /> : 'Save Mappings'}
        </Button>
      </Box>
    </Box>
  )
}

export default VehicleMapping
